import { Box, Button, Flex, Heading, Stack, Text, useDisclosure, useToast, Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton } from '@chakra-ui/react';
import { Edit2, BookOpen, MessageSquare, PhoneForwarded, Settings, AlertTriangle } from 'lucide-react';
import { Alert, AlertIcon } from '@chakra-ui/react';
import { CallServiceType, IAgent, IAgentVoices, PhoneStatusType } from 'types';
import VoicePicker from './VoicePicker';
import { PATHS } from 'router/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import UnCancelSubscriptionModal from 'components/modals/UnCancelSubscriptionModal';
import {
    useChangeCallServiceByAgentIdMutation,
    usePreventAutomaticCancellationMutation,
} from 'graphql/generated';
import { useError } from 'error-provider/ErrorProvider';
import { useApolloClient } from '@apollo/client';
import TimeZone from './TimeZone';
import ChangeTitle from 'components/modals/ChangeTitle';
import BrowserCall from './BrowserCall';
import BuyPhoneNumberModal from 'components/modals/BuyPhoneNumberModal';
import PhoneStatus from './PhoneStatus';
import ActiveTools from './ActiveTools';
import useServerApi from 'hooks/useServerApi';
import { useUserData } from 'context/UserDataProvider';
import type { UserData } from 'hooks/useGetUserData';
import AgentGoogleCalendar from './AgentGoogleCalendar.jsx';

interface Props extends IAgent {
    agentVoices: IAgentVoices;
    currentMinutes: number;
    isLoadingCurrentMinutes: boolean;
    myEmail: string;
    phoneStatus: PhoneStatusType;
}



const Agent = ({
    agentVoices,
    currentMinutes,
    isLoadingCurrentMinutes,
    myEmail,
    title,
    id,
    name,
    phoneNumber = '',
    userSubscriptionCancelDate,
    timeZone,
    callService,
    phoneStatus,
    ...props
}: Props) => {
    // Get myUserData from context, following MyAgent.tsx pattern
    const { userData: userDataFromContext } = useUserData();

    const userData = userDataFromContext as UserData | null;
    const { isOpen: isOpenChangeTitle, onClose: onCloseChangeTitle, onOpen: onOpenChangeTitle } = useDisclosure();
    const { isOpen: isOpenChooseNumber, onClose: onCloseChooseNumber } = useDisclosure();
    const toast = useToast();
    const apolloClient = useApolloClient();
    const { showError } = useError();
    const router = useNavigate();
    const [searchParams] = useSearchParams();
    const {
        isOpen: isOpenUnCancelSubscriptionModal,
        onClose: onCloseUnCancelSubscriptionModal,
    } = useDisclosure();

    const [integratedTools, setIntegratedTools] = useState({
        call_transfers: false,
        calendar_booking: false,
        integration_specifics: {
            call_transfers: {},
            calendar_booking: {},
        },
        fetched: false,
    });


    const [currentVoices, setCurrentVoices] = useState<string[]>([]);

    const [showAgentGoogleCalendar, setShowAgentGoogleCalendar] = useState(false);


    const [preventAutomaticCancellation] = usePreventAutomaticCancellationMutation({
        onError(error) {
            showError(undefined, error);
        },
    });

    useChangeCallServiceByAgentIdMutation({
        onError(error) {
            showError(undefined, error);
        },
    });

    const [{ get }] = useServerApi();

    useEffect(() => {
        const fetchIntegratedTools = async () => {
            if (!id || integratedTools.fetched) return;
            const [success, data] = await get(`/rest_endpoints/${id}/vapi/integrated_tools`);
            setIntegratedTools((prev) => ({
                ...prev,
                fetched: true,
            }));
            if (success && data && data.data) {
                setIntegratedTools({
                    call_transfers: !!data.data.call_transfers,
                    calendar_booking: !!data.data.calendar_tool,
                    integration_specifics: {
                        call_transfers: {},
                        calendar_booking: {},
                    },
                    fetched: true,
                });
            }
        }
        fetchIntegratedTools();
    }, [id, integratedTools.fetched, get]);

    useEffect(() => {
        const fetchAgentVoices = async () => {
            if (!id) return;
            const [success, data] = await get(`/rest_endpoints/agents/crud/voices/${id}`);
            if (success && data && data.voices) {
                const voiceNames = data.voices.map((voice: any) => voice.name);
                setCurrentVoices(voiceNames);
            }
        };
        fetchAgentVoices();
    }, [id, get]);

    useEffect(() => {
        if (searchParams.get('openCalendarSettings') === 'true') {
            setShowAgentGoogleCalendar(true);
        }
    }, [searchParams]);

    useEffect(() => {
        const matchingSub = ['localhost', 'app.dialzara.com', 'dev-app.dialzara.com'].find(sub => window.location.host.includes(sub));
        if (matchingSub) {
            console.log('Fetching token for:', window.location.host);
            const fetchToken = async () => {
                const [success, response] = await get('/rest_endpoints/accounts/generate_user_token');
                if (success && response.token) {
                    if (matchingSub === 'localhost') {
                        console.log('token matched for localhost:', response.token);
                        console.log('localhost:3000/sign-in-with-token?token=' + response.token.trim());
                    }
                    else if (window.location.host === 'dev-app.dialzara.com') {
                        window.location.href = 'https://dev-preview.dialzara.com/sign-in-with-token?token=' + response.token.trim();
                    }
                    else if (window.location.host === 'app.dialzara.com') {
                        window.location.href = 'https://web.dialzara.com/sign-in-with-token?token=' + response.token.trim();
                    }
                }
            };
            fetchToken();
        }
    }, [get]);

    const onConfirmCancelSubscription = async () => {
        try {
            onCloseUnCancelSubscriptionModal();
            await preventAutomaticCancellation({
                variables: { email: myEmail },
                onCompleted(data) {
                    if (data.preventAutomaticCancellation.success) {
                        apolloClient.cache.modify({
                            id: `AgentType:${id}`,
                            fields: {
                                userSubscriptionCancelDate() {
                                    return '';
                                },
                            },
                        });
                        toast({ status: 'success', title: 'Success!', isClosable: true });
                    } else {
                        toast({
                            status: 'error',
                            title: `Error: ${data.preventAutomaticCancellation.error}`,
                            isClosable: true,
                        });
                    }
                },
            });
        } finally {
            // Cleanup if needed
        }
    };

    const AGENT_ACTIONS = [
        {
            name: 'Agent Configuration',
            icon: <BookOpen className='w-4 h-4 mb-1' />,
            action: () => router(`/agent-configuration/${id}/training`),
        },
        {
            name: 'Simulated Chat',
            icon: <MessageSquare className='w-4 h-4 mb-1' />,
            action: () => router(`/agent-configuration/${id}/chat`),
        },
        {
            name: 'Call Transfers',
            icon: <PhoneForwarded className='w-4 h-4 mb-1' />,
            action: () => router(`/agent-configuration/${id}/transfers`),
        },
        {
            name: 'More Settings',
            icon: <Settings className='w-4 h-4 mb-1' />,
            action: () => router(`/agent-configuration/${id}/more-settings`),
        },
    ];

    const deriveSubscriptionStatus = useMemo(() => {
        return userData?.db_subscription_information != null
            ? userData.db_subscription_information.status
            : 'none';
    }, [userData]);

    const allowMultipleVoices = useMemo(() => {
        return userData?.has_premium_subscription ?? false;
    }, [userData]);


    const subscriptionStatus = deriveSubscriptionStatus;
    if (subscriptionStatus === 'none') {
        phoneStatus = PhoneStatusType.demo_number;
    }
    else if (!phoneNumber) {
        phoneStatus = PhoneStatusType.buy_number;
    }

    const isDemoNumber = phoneStatus === PhoneStatusType.demo_number;

    const redirectToSubscription = () => {
        if (subscriptionStatus === 'none') {
            router(PATHS.SUBSCRIPTION_PLAN);
        }
        else if (!phoneNumber) {
            router(PATHS.PHONE_PICK);
        }
    };

    function formatPhoneNumber(rawNumber?: string): string {
        if (!rawNumber) return '';
        let num = rawNumber;
        if (num.startsWith('+1')) {
            num = num.slice(2);
        } else if (num.startsWith('1') && num.length === 11) {
            num = num.slice(1);
        }
        return num;
    }

    const formattedPhoneNumber = formatPhoneNumber(userData?.user_info?.phone_number);

    return (
        <Box
            className='mb-4'
            backgroundColor='white'
            borderRadius='md'
            border='1px solid'
            borderColor='lightgray'
            p='5'
        >
            <div className='flex justify-between items-center'>
                <Heading fontSize='large' display='flex' alignItems='center' justifyContent='center'>
                    {title}
                    <Box ml={2}>
                        {callService === CallServiceType.VAPI && currentMinutes > 1 && <BrowserCall id={id} type={`vapi`} />}
                    </Box>
                </Heading>
                <Button variant='outline' size='sm' onClick={onOpenChangeTitle}>
                    <Edit2 className='w-4 h-4 mr-2' />
                    Edit
                </Button>
            </div>
            {!isLoadingCurrentMinutes && currentMinutes <= 0 && (subscriptionStatus === 'trial' || subscriptionStatus === 'paid') && (
                <Flex
                    borderRadius='6px'
                    p='10px'
                    gap='6'
                    justifyContent='space-between'
                    alignItems='center'
                    border='1px solid orange'
                    bgColor='moccasin'
                    maxW='lg'
                >
                    <Text color='#994C00'>This agent has 0 call minutes left.</Text>
                    <Button
                        onClick={() => router(PATHS.SUBSCRIPTION)}
                        _hover={{
                            bgColor: 'darkorange',
                        }}
                        _active={{
                            bgColor: 'coral',
                        }}
                        color='white'
                        bgColor='orange'
                    >
                        Add more minutes
                    </Button>
                </Flex>
            )}


            {/* Move Voice/Phone/TimeZone row here, styled like section headers */}
            <Box mt={6}>
                <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
                    <div className='space-y-1'>
                        <Text fontWeight="bold" fontSize="md" mb={2}>
                            Voice
                        </Text>
                        <VoicePicker
                            agentId={id}
                            agentVoices={agentVoices}
                            name={name}
                            allowMultipleSelect={allowMultipleVoices}
                            currentVoices={currentVoices}
                        />
                    </div>
                    <Stack spacing='1' justifyContent='space-between'>
                        <Flex alignItems="center" gap={2}>
                            <Text fontWeight="bold" fontSize="md" mb={2}>
                                Phone Number
                            </Text>
                            {/* Limited Access Alert Badge */}
                            {isDemoNumber && (<Alert
                                status='error'
                                borderRadius='md'
                                height='22px'
                                width='fit-content'
                                display='inline-flex'
                                alignItems='center'
                                variant='solid'
                                bg='red.500'
                                color='white'
                                style={{ padding: '0 8px', fontSize: '0.88rem', fontWeight: 600, lineHeight: 1.1 }}
                            >
                                <AlertIcon boxSize='14px' mr={1} />
                                Limited Access
                            </Alert>)}
                        </Flex>
                        <PhoneStatus
                            phoneNumber={phoneNumber}
                            phoneStatus={phoneStatus}
                            onBuyNumber={redirectToSubscription}
                        />

                    </Stack>
                    <div className='space-y-1'>
                        <Text fontWeight="bold" fontSize="md" mb={2}>
                            Time Zone
                        </Text>
                        <TimeZone agentId={id} timeZone={timeZone} />
                    </div>
                </div>
            </Box>
            {isDemoNumber && (
                <Box
                    mt={6}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="orange.300"
                    bg="#fdf9ed"
                    p={4}
                    w="full"
                >
                    <Flex alignItems="center" mb={2}>
                        <Box mr={2}>
                            <AlertTriangle color="#dd6b20" size={20} />
                        </Box>
                        <Text fontWeight="bold" color="orange.700">Testing Number - Limited Access</Text>
                    </Flex>
                    <Stack spacing='3' >
                        <Text color="red.600" fontWeight="bold">
                            • This is a <Box as="span" fontWeight={700}>testing number</Box>
                        </Text>
                        <Text color="red.600">
                            • It will <Box as="span" fontWeight={700}>only work</Box> when you call from your registered phone number: <Box as="span" fontWeight={700}>{formattedPhoneNumber ? `(${formattedPhoneNumber.slice(0, 3)}) ${formattedPhoneNumber.slice(3, 6)}-${formattedPhoneNumber.slice(6)}` : ''}</Box>
                        </Text>
                        <Text color="red.600">
                            • To get a <Box as="span" fontWeight={700}>unique, dedicated number</Box>, add a credit card and start your free trial
                        </Text>
                    </Stack>
                </Box>
            )}

            {/* Active Tools section next */}
            <Stack spacing='3' mt={6}>
                <ActiveTools
                    call_transfers={{
                        enabled: integratedTools.call_transfers,
                        action: () => router(`/agent-configuration/${id}/transfers`),
                    }}
                    calendar_booking={{
                        enabled: integratedTools.calendar_booking,
                        action: () => setShowAgentGoogleCalendar(true),
                    }}
                    integration_specifics={integratedTools.integration_specifics}
                />
                {/* Boxes for configuration, chat, etc. */}
                <div className='grid gap-2 md:grid-cols-2 lg:grid-cols-4'>
                    {AGENT_ACTIONS.map((action) => (
                        <Button key={action.name} variant='outline' size='full' onClick={action.action}>
                            <Stack alignItems='center' py='3'>
                                {action.icon}
                                <span className='text-[13px]'>{action.name}</span>
                            </Stack>
                        </Button>
                    ))}
                </div>
            </Stack>

            {isOpenUnCancelSubscriptionModal && (
                <UnCancelSubscriptionModal
                    isOpen={isOpenUnCancelSubscriptionModal}
                    onClose={onCloseUnCancelSubscriptionModal}
                    onConfirm={onConfirmCancelSubscription}
                />
            )}

            {isOpenChooseNumber && (
                <BuyPhoneNumberModal isOpen={isOpenChooseNumber} onClose={onCloseChooseNumber} agentId={id} />
            )}

            <ChangeTitle onClose={onCloseChangeTitle} isOpen={isOpenChangeTitle} name={title} agentId={id} />

            {showAgentGoogleCalendar && <AgentGoogleCalendar onClose={() => setShowAgentGoogleCalendar(false)} />}
        </Box>
    );
};

export default Agent;
